import { LytefloLoadingScreen } from "@components/LytefloLoadingScreen";
import { usePortalPermissions } from "@hooks/usePortalPermissions";
import { useRolloutFlagsReady } from "@hooks/useRolloutFlagsReady";
import { type FeatureRequirement, PortalRoutes } from "@lyteflo/types";
import {
  toFeatureList,
  useDealerContextReady,
  useDealerGroups,
  useEntitlementsReady,
  useProfileStore,
  useToast,
} from "@lyteflo/utils";
import type { ComponentChildren } from "preact";
import { useEffect } from "preact/compat";
import { useTranslation } from "react-i18next";
import { Redirect, useLocation } from "wouter";

interface ProtectedRouteProps {
  children: ComponentChildren;
  feature?: FeatureRequirement;
}

export const ProtectedRoute = ({ children, feature }: ProtectedRouteProps) => {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { hasAnyPermission } = usePortalPermissions();
  const { showError } = useToast();
  const { isAuthenticated, isLoading, fetchProfile } = useProfileStore();
  const isDealerContextReady = useDealerContextReady();
  const isEntitlementReady = useEntitlementsReady(feature);
  const areRolloutFlagsReady = useRolloutFlagsReady(feature);
  const {
    isError: dealerGroupsFailed,
    isCached: dealerGroupsCached,
    isLoading: dealerGroupsLoading,
  } = useDealerGroups(isAuthenticated);

  useEffect(() => {
    if (
      isAuthenticated &&
      dealerGroupsFailed &&
      !dealerGroupsCached &&
      !dealerGroupsLoading
    ) {
      showError(t("auth.error.sessionLoadFailed"), {
        deduplicationId: "dealer-groups-fetch-error",
      });
      setLocation(PortalRoutes.LOGOUT);
    }
  }, [
    isAuthenticated,
    dealerGroupsFailed,
    dealerGroupsCached,
    dealerGroupsLoading,
    showError,
    t,
    setLocation,
  ]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      const verifyAuth = async () => {
        // Only answers "is there a session" — the permission decision belongs
        // to the post-auth guard below, which waits for readiness. Deciding
        // here would use the hasPermission captured before the profile landed,
        // and would run before the scoped entitlement read.
        if (!(await fetchProfile())) setLocation(PortalRoutes.LOGOUT);
      };
      verifyAuth();
    }
  }, [isAuthenticated, isLoading, fetchProfile, setLocation]);

  // The entitlement and the rollout flags behind a feature both arrive a
  // round-trip after the profile that bootstraps the session, so deciding
  // before they land would refuse a dealership its own page.
  if (
    isLoading ||
    (isAuthenticated &&
      (!isDealerContextReady || !isEntitlementReady || !areRolloutFlagsReady))
  ) {
    return <LytefloLoadingScreen />;
  }

  if (!isAuthenticated) {
    return null;
  }

  // Checked here rather than only while bootstrapping: a restored session never
  // runs that path, which left every feature-gated route reachable by typing its
  // URL while the nav merely hid the link.
  // Redirect rather than setLocation: navigating from the render body mutates
  // the router mid-render, and a push would trap Back — returning to this route
  // only to be sent forward again.
  if (feature && !hasAnyPermission(toFeatureList(feature))) {
    return <Redirect to={PortalRoutes.HOME} replace />;
  }

  return children;
};
